import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Cache for loaded PDF covers
const coverCache = new Map();

const PDF_OPTIONS = {
  cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
  cMapPacked: true,
  disableAutoFetch: true,
  disableStream: true,
  disableRange: true,
  disableFontFace: true,
  disableCreateObjectURL: true,
  disableWebGL: true,
  disableWorker: false,
  /* Allow larger images so PDF cover art is not removed (avoids "Image exceeded maximum allowed size" in console) */
  maxImageSize: 8 * 1024 * 1024, // 8MB
  isEvalSupported: false,
  useSystemFonts: true,
  standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/'
};

const PDFCover = ({ pdfUrl, width = 200, height = 280, className = '', alt = 'PDF Cover', lazy = true, fitWidthOnly = false }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(!lazy);
  const [cachedImage, setCachedImage] = useState(null);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const canvasRef = useRef(null);
  // Custom canvas cover (fitWidthOnly): we draw first page ourselves so any first-page size works
  const [coverViewport, setCoverViewport] = useState(null);
  const pdfDocRef = useRef(null);

  // Check cache first
  useEffect(() => {
    if (coverCache.has(pdfUrl)) {
      setCachedImage(coverCache.get(pdfUrl));
      setLoading(false);
      return;
    }
  }, [pdfUrl]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || !containerRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    observerRef.current.observe(containerRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [lazy]);

  // Custom cover: load PDF and get viewport so first page fills the box (works for any first-page size)
  useEffect(() => {
    if (!fitWidthOnly || !isVisible || !pdfUrl) return;

    setLoading(true);
    let cancelled = false;
    let doc = null;

    const load = async () => {
      try {
        doc = await pdfjs.getDocument({ url: pdfUrl, ...PDF_OPTIONS }).promise;
        if (cancelled) {
          doc.destroy();
          return;
        }
        const page = await doc.getPage(1);
        const vp1 = page.getViewport({ scale: 1 });
        const scale = Math.min(3, Math.max(width / vp1.width, height / vp1.height));
        const viewport = page.getViewport({ scale });
        if (cancelled) {
          doc.destroy();
          return;
        }
        pdfDocRef.current = doc;
        setCoverViewport(viewport);
        setLoading(false);
        if (pdfUrl && !coverCache.has(pdfUrl)) coverCache.set(pdfUrl, true);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading PDF cover:', err);
          setError(err);
          setLoading(false);
        } else if (doc) doc.destroy();
      }
    };

    load();

    return () => {
      cancelled = true;
      if (doc && !pdfDocRef.current) doc.destroy();
    };
  }, [fitWidthOnly, isVisible, pdfUrl, width, height]);

  // Custom cover: draw page 1 to canvas when we have canvas ref and viewport
  useEffect(() => {
    if (!fitWidthOnly || !pdfDocRef.current || !coverViewport || !canvasRef.current) return;

    const doc = pdfDocRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const vw = Math.round(coverViewport.width);
    const vh = Math.round(coverViewport.height);
    canvas.width = vw;
    canvas.height = vh;

    let cancelled = false;
    doc.getPage(1).then((page) => {
      if (cancelled) {
        page.cleanup();
        return;
      }
      return page.render({
        canvasContext: ctx,
        viewport: coverViewport,
        background: 'rgba(240,240,240,1)'
      }).promise.then(() => {
        page.cleanup();
      });
    }).catch((err) => {
      if (!cancelled) {
        console.error('Error rendering PDF page:', err);
        setError(err);
      }
    });

    return () => { cancelled = true; };
  }, [fitWidthOnly, coverViewport]);

  // Cleanup PDF doc on unmount or url change (custom cover)
  useEffect(() => {
    if (!fitWidthOnly) return;
    return () => {
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
      setCoverViewport(null);
    };
  }, [pdfUrl, fitWidthOnly]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error('Error loading PDF:', error);
    setError(error);
    setLoading(false);
  }, []);

  const onPageLoadSuccess = useCallback(() => {
    setLoading(false);
    if (pdfUrl && !coverCache.has(pdfUrl)) {
      coverCache.set(pdfUrl, true);
    }
  }, [pdfUrl]);

  const onPageLoadError = useCallback((error) => {
    console.error('Error loading PDF page:', error);
    setError(error);
    setLoading(false);
  }, []);

  // For custom cover: size canvas so it "covers" the box (centered, overflow clipped)
  const coverDisplaySize = fitWidthOnly && coverViewport
    ? (() => {
        const vw = coverViewport.width;
        const vh = coverViewport.height;
        const scale = Math.max(width / vw, height / vh);
        return { width: Math.ceil(vw * scale), height: Math.ceil(vh * scale) };
      })()
    : null;

  if (error) {
    return (
      <div 
        className={`d-flex align-items-center justify-content-center bg-light ${className}`}
        style={{ 
          width, 
          height,
          backgroundColor: '#f8f9fa'
        }}
      >
        <div className="text-center text-muted">
          <i className="fas fa-file-pdf fa-2x mb-2 text-primary"></i>
          <div className="small fw-medium">PDF Cover</div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`position-relative ${className}`} 
      style={{ 
        width, 
        height, 
        overflow: 'hidden',
        backgroundColor: '#f0f0f0'
      }}
    >
      {loading && (
        <div 
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light"
          style={{ 
            zIndex: 1,
            backgroundColor: '#f8f9fa'
          }}
        >
          <div className="text-center">
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="small text-muted">Loading...</div>
          </div>
        </div>
      )}
      
      {isVisible && (fitWidthOnly && coverViewport ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              flexShrink: 0,
              maxWidth: '100%',
              maxHeight: '100%',
              width: coverDisplaySize ? coverDisplaySize.width : width,
              height: coverDisplaySize ? coverDisplaySize.height : height,
              display: 'block',
              backgroundColor: '#f0f0f0'
            }}
            aria-label={alt}
          />
        </div>
      ) : !fitWidthOnly && (
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
          error=""
          options={PDF_OPTIONS}
        >
          <Page
            pageNumber={pageNumber}
            width={width}
            {...(height != null && { height })}
            onLoadSuccess={onPageLoadSuccess}
            onLoadError={onPageLoadError}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            renderMode="canvas"
            canvasBackground="transparent"
          />
        </Document>
      ))}
      
      {!isVisible && lazy && (
        <div 
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light"
          style={{ 
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}
        >
          <div className="text-center text-muted">
            <i className="fas fa-file-pdf fa-2x mb-2"></i>
            <div className="small">PDF Cover</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFCover;
